'use client';

import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useAudioPlayer } from '@/context/AudioPlayerContext';

export default function GlobalPlayerBar() {
  const { currentEpisode, isPlaying, togglePlay, isVideoModalOpen, setIsVideoModalOpen } = useAudioPlayer();

  if (!currentEpisode) return null;

  return (
    <>
      {/* Video Modal if video content */}
      {isVideoModalOpen && currentEpisode.media_type === 'video' && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
          <div className="relative w-full max-w-4xl bg-slate-900 rounded-2xl overflow-hidden border border-purple-500/20 shadow-2xl">
            <div className="flex items-center justify-between p-4 bg-slate-950 border-b border-slate-800">
              <div className="flex items-center space-x-3">
                <span className="px-2.5 py-1 bg-pink-500 text-white rounded text-xs font-bold uppercase">Video Episode</span>
                <h3 className="font-heading font-bold text-white text-lg truncate">{currentEpisode.title}</h3>
              </div>
              <button
                onClick={() => setIsVideoModalOpen(false)}
                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
              >
                <Icon name="XMarkIcon" size={24} />
              </button>
            </div>
            <div className="aspect-video bg-black flex items-center justify-center">
              <video
                src={currentEpisode.media_url}
                controls
                autoPlay
                className="w-full h-full object-contain"
              />
            </div>
            <div className="p-4 bg-slate-950 flex items-center justify-between text-sm text-gray-400">
              <span>{currentEpisode.channel?.channel_name || 'KissaFM Creator'}</span>
              <span>{currentEpisode.views_count?.toLocaleString()} Views</span>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Sticky Player Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-slate-950/95 backdrop-blur-md border-t border-purple-500/20 text-white py-3 px-4 shadow-2xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          {/* Track Info */}
          <div className="flex items-center space-x-3 min-w-0 w-1/3">
            <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 relative border border-purple-500/30">
              <AppImage
                src={currentEpisode.thumbnail_url || '/assets/images/Logo_main.jpeg'}
                alt={currentEpisode.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="min-w-0">
              <p className="font-heading font-bold text-sm text-white truncate">{currentEpisode.title}</p>
              <p className="text-xs text-purple-300 truncate">{currentEpisode.channel?.channel_name || 'KissaFM Channel'}</p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center space-x-4 w-1/3">
            <button className="text-gray-400 hover:text-white transition-colors">
              <Icon name="BackwardIcon" size={20} />
            </button>

            <button
              onClick={togglePlay}
              className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 flex items-center justify-center text-white shadow-lg hover:scale-105 transition-all"
            >
              <Icon name={isPlaying ? 'PauseIcon' : 'PlayIcon'} size={22} variant="solid" />
            </button>

            <button className="text-gray-400 hover:text-white transition-colors">
              <Icon name="ForwardIcon" size={20} />
            </button>
          </div>

          {/* Video Toggle & Details Link */}
          <div className="flex items-center justify-end space-x-3 w-1/3">
            {currentEpisode.media_type === 'video' && (
              <button
                onClick={() => setIsVideoModalOpen(true)}
                className="flex items-center space-x-1 px-3 py-1.5 bg-purple-600/30 text-purple-300 border border-purple-500/40 rounded-lg text-xs font-semibold hover:bg-purple-600/50 transition-all"
              >
                <Icon name="VideoCameraIcon" size={16} />
                <span>Watch Video</span>
              </button>
            )}
            <a
              href={`/episode/${currentEpisode.id}`}
              className="text-xs text-gray-400 hover:text-purple-400 transition-colors hidden sm:block"
            >
              Full Details ➔
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
